import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_URL, TEST_LOGIN_DOMAIN } from '../../../config';
import {
  getOrgPrefix,
  generateFunName,
  generatePassphrase,
} from '../../../utils/provision-email-user';
import type { User } from '../../../contexts/AuthContext';

export type ProvisionedEmail = {
  email: string;
  funName: string;
  passphrase: string;
};

type UseProvisionEmailReturn = {
  provision: () => Promise<ProvisionedEmail | null>;
  isProvisioning: boolean;
  error: string | null;
  clearError: () => void;
};

/**
 * Shared hook that generates a unique `{funName}@TEST_LOGIN_DOMAIN` address
 * by probing the recipient availability endpoint. Returns null (and sets
 * `error`) if provisioning is misconfigured or a unique name can't be found.
 */
export function useProvisionEmail(): UseProvisionEmailReturn {
  const qc = useQueryClient();
  const [isProvisioning, setIsProvisioning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const provision = React.useCallback(async (): Promise<ProvisionedEmail | null> => {
    if (!TEST_LOGIN_DOMAIN) {
      setError('Email provisioning is not configured (no TEST_LOGIN_DOMAIN)');
      return null;
    }

    setIsProvisioning(true);
    setError(null);

    try {
      const meData = qc.getQueryData<User | null>(['me']);
      const orgName = meData?.organizations?.find((o) => o.id === meData?.orgId)?.name ?? 'org';
      const prefix = getOrgPrefix(orgName);

      for (let attempt = 0; attempt < 5; attempt++) {
        const funName = generateFunName(prefix);
        const email = `${funName}@${TEST_LOGIN_DOMAIN}`;

        const res = await fetch(
          `${API_URL}/email/recipients/check-email?email=${encodeURIComponent(email)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error('Failed to check email availability');
        const data = await res.json();

        if (data.available) {
          return { email, funName, passphrase: generatePassphrase() };
        }
      }

      setError('Could not generate a unique email after 5 attempts. Please try again.');
      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to provision email');
      return null;
    } finally {
      setIsProvisioning(false);
    }
  }, [qc]);

  const clearError = React.useCallback(() => setError(null), []);

  return { provision, isProvisioning, error, clearError };
}
